import { FieldsProvider } from './Fields.Context';
import type { FieldsProviderProps } from './Fields.Context';
import { EditorWrapper } from '../Editor';
import { FieldsSectionEditor } from './Fields.SectionEditor';
import { FieldsSubmit } from './Fields.Submit';
import { FieldsAddendumEditor } from './Fields.AddendumEditor';
import { FieldsAddendumRenderer } from './Fields.AddendumRenderer';
import { useRecoilValue } from 'recoil';
import { ReportStatuses, reportStatusState, SECTION_EDITOR_EDITABLE_STATUSES } from '../state';
import { FieldsSectionRenderer } from './Fields.SectionRenderer';
import { useCurrentCaseReport } from 'hooks/useCurrentCaseReport';
import { Stack } from 'common/ui/Layout';
import { useCurrentCaseGroupSubmitting } from '../hooks/useCurrentCaseGroupSubmitting';
import { telemetryContextFor } from 'modules/Apollo/types';
import { FieldsSubmitToggleRow } from './Fields.SubmitToggleRow';

export type FieldsProps = FieldsProviderProps & {
  isLoading?: boolean;
  children?: React.ReactNode | undefined;
};

export const Fields = ({ isLoading, ...props }: FieldsProps): React.ReactElement => {
  const status = useRecoilValue(reportStatusState);
  const { hasSubmittingCaseInGroup } = useCurrentCaseGroupSubmitting({
    context: APOLLO_QUERY_CONTEXT,
  });
  const isReportEditable =
    (SECTION_EDITOR_EDITABLE_STATUSES as ReportStatuses[]).includes(status) &&
    !hasSubmittingCaseInGroup;
  const { currentCaseReport } = useCurrentCaseReport();

  return (
    <FieldsProvider {...props}>
      <EditorWrapper variant="report" space="xsmall">
        <Stack
          data-stack-stretch
          space="medium"
          stretchX
          stretchY
          css="overflow: auto; height: 100%; min-height: 55%;"
        >
          {isReportEditable ? (
            <FieldsSectionEditor
              isDisabled={props.isDisabled}
              onInit={props.onInit}
              onDestroy={props.onDestroy}
              autoloadTemplateState={props.autoloadTemplateState}
              isLoading={isLoading}
            />
          ) : (
            <FieldsSectionRenderer />
          )}
        </Stack>
        <Stack vertical space="small">
          <FieldsAddendumRenderer isClaimedByMe={props.isClaimedByMe} />
          <FieldsAddendumEditor
            isClaimedByMe={props.isClaimedByMe}
            isDisabled={props.isDisabled}
            onInit={props.onInit}
          />
          <FieldsSubmit
            isDisabled={props.isDisabled}
            isClaimedByMe={props.isClaimedByMe}
            smid={currentCaseReport?.smid}
            initialExamCount={currentCaseReport?.examCount ?? 1}
          />
          <FieldsSubmitToggleRow />
        </Stack>
      </EditorWrapper>
    </FieldsProvider>
  );
};

Fields.displayName = 'Fields';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(Fields);
